import { A } from '@solidjs/router';
import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { approveAdminUser, suspendAdminUser, type AdminUserRow as UserRow } from '../lib/admin';
import { boxBudgetCopy } from '../lib/admin-provision';
import { grantSubjectHref } from '../lib/adminGrants';
import { createConfirmGate } from '../lib/confirm-gate';
import { createKeyedMutation } from '../lib/keyed-mutation';
import type { OperatorOutcome, OperatorRefusal } from '../lib/operator-credential';
import { ConfirmDialog } from './ConfirmDialog';
import { OperatorRefusalAlert } from './OperatorRefusalAlert';
import { Button, buttonClass } from './ui/Button';

// The actions cell of one admin user-registry row. Approve / suspend are the
// REST twins `POST /v1/admin/users/:id/{approve,suspend}` (`lib/admin.ts`) —
// one control behind the MCP `user approve|suspend` actions and this door. Both
// ride an `admin:users` operator credential (#5724); a door refusal renders
// `<OperatorRefusalAlert/>` and sends nothing, any other refusal the API's own
// `detail`. On success `onMutated` re-reads the registry.
//
// SUSPEND ASKS FIRST (plan 101 slice 17 row 17-16, `createConfirmGate`): it caps
// every session the person holds, its MCP twin demands `confirm: true`, and it
// sat one click away beside Grant. Approve does not ask — it is the benign half and idempotent.
//
// APPROVE FUNDS FIRST (plan 102 slice 05): with no live capacity grant the route
// grants the invite bundle, and only then approves (`lib/admin.ts` `AdminUserApproval`).
// Its 422 means the bundle was refused and the person was NOT approved: the server's
// sentence is the row's error, the person stays waitlisted, and the registry still
// re-reads (the refusal is audited server-side). A funded approve says the box budget.
//
// GRANT LINKS, it does not duplicate. `/admin/grants` is subject-addressed and
// reads its subject off the URL (`<AdminGrantsPanel/>`), so this cell hands it
// the person it already resolved — the one shape that cannot be mistyped.

export function AdminUserActions(props: {
  row: UserRow;
  token: string | null;
  onMutated: () => void;
}): JSX.Element {
  const approved = (): boolean => props.row.approved_at_iso !== null;
  const [refusal, setRefusal] = createSignal<OperatorRefusal | null>(null);
  const [notApproved, setNotApproved] = createSignal<string | null>(null);
  const [funded, setFunded] = createSignal<string | null>(null);
  const act = createKeyedMutation({
    fallback: t('admin.write_error'),
    onStart: () => {
      setRefusal(null);
      setNotApproved(null);
      setFunded(null);
    },
  });
  const busy = (): boolean => act.isBusy('approve') || act.isBusy('suspend');
  const landed = (out: OperatorOutcome<unknown>): void =>
    out.kind === 'ok' ? props.onMutated() : void setRefusal(out.kind);

  const approve = (): Promise<void> =>
    act.run(
      'approve',
      async () => {
        const out = await approveAdminUser(props.row.id, props.token);
        if (out.kind === 'ok' && out.value.kind === 'not_approved') {
          setNotApproved(out.value.detail);
        }
        if (out.kind === 'ok' && out.value.kind === 'approved' && out.value.boxes !== undefined) {
          const budget = boxBudgetCopy(out.value.boxes);
          setFunded(t('admin.user_approved_funded', { login: props.row.login, budget }));
        }
        landed(out);
      },
      t('admin.user_approve_failed'),
    );
  // The row the confirm was ASKED about, not whatever `props.row` is when it lands.
  const suspend = (row: UserRow): Promise<void> =>
    act.run(
      'suspend',
      async () => landed(await suspendAdminUser(row.id, props.token)),
      t('admin.user_suspend_failed'),
    );

  const asking = createConfirmGate<UserRow>({ act: suspend });

  const grantHref = (): string =>
    grantSubjectHref({ kind: 'user', id: props.row.id, label: props.row.login });

  return (
    <>
      <div class="admin-row__controls">
        <div class="data-table__actions">
          <Show
            when={approved()}
            fallback={
              <Button variant="outline" size="sm" disabled={busy()} onClick={() => void approve()}>
                {t('admin.user_approve')}
              </Button>
            }
          >
            <Button
              variant="destructive"
              size="sm"
              disabled={busy()}
              onClick={() => asking.ask(props.row)}
            >
              {t('admin.user_suspend')}
            </Button>
          </Show>
          <A class={buttonClass({ variant: 'outline', size: 'sm' })} href={grantHref()}>
            {t('admin.user_grant')}
          </A>
        </div>
        <OperatorRefusalAlert refusal={refusal()} />
        <Show when={funded()}>
          {(line) => (
            <p class="field__desc" role="status">
              {line()}
            </p>
          )}
        </Show>
        <Show when={notApproved()}>
          {(detail) => (
            <p class="field__error" role="alert">
              {detail()}
            </p>
          )}
        </Show>
        <Show when={act.error()}>
          {(msg) => (
            <p class="field__error" role="alert">
              {msg()}
            </p>
          )}
        </Show>
      </div>
      <ConfirmDialog
        open={asking.pending() !== null}
        title={t('admin.user_suspend_title', { login: props.row.login })}
        desc={t('admin.user_suspend_desc')}
        cancelLabel={t('admin.confirm_cancel')}
        actionLabel={t('admin.user_suspend_confirm')}
        destructive
        onCancel={asking.cancel}
        onConfirm={() => void asking.confirm()}
      />
    </>
  );
}
